// Plain-text preview for the chat markdown subset.
//
// PURE module — no Angular imports — so it can be exercised by a plain Node
// harness, exactly like `chat-markdown.renderer.ts` next to it.
//
// This is the renderer's mirror image. The renderer turns the markers into HTML
// for surfaces that can show it; this STRIPS them for the surfaces that cannot:
// the conversation list, the quote strip, the pinned bar, the forward dialog,
// the switcher and the search excerpts. Those all render inside a single
// `text-truncate` line, so a message written as
//
//   **Urgente**: revisar o [relatório](https://…/reports/42)
//
// used to preview literally, asterisks/brackets/URL and all.
//
// KEEP IN SYNC with `chat-markdown.renderer.ts` — same subset, and the regexes
// below are deliberately the same shape (notably the italic rule, whose word
// boundaries are what keep `snake_case` identifiers literal).

/**
 * Media placeholders the backend persists into `lastMessagePreview`
 * (`buildChatMessagePreview` in trendmulti-chat/util/chat-content.util.ts).
 *
 * They are stored as literal ASCII tokens and were reaching the UI unchanged —
 * a row for a photo read `[image]`. Mapping happens here, on read, because the
 * tokens are already persisted in every channel document: translating them at
 * write time would only fix conversations created from now on, and would bake a
 * single language into shared data that each member reads in their own.
 */
const MEDIA_PREVIEWS: Readonly<
  Record<string, { labelKey: string; icon: string }>
> = {
  '[image]': { labelKey: 'chat.preview.image', icon: 'ri-image-line' },
  '[audio]': { labelKey: 'chat.preview.audio', icon: 'ri-mic-line' },
  '[video]': { labelKey: 'chat.preview.video', icon: 'ri-vidicon-line' },
  '[file]': { labelKey: 'chat.preview.file', icon: 'ri-file-3-line' },
  // Work-reference message with no accompanying text (RF-35) — the row used to
  // preview an empty string. Same default glyph the reference card uses.
  '[contextRef]': { labelKey: 'chat.preview.contextRef', icon: 'ri-links-line' },
  // Deleted-by-author tombstone (RF-10) — persisted when the deleted message
  // was the conversation's last, mirroring the in-flow "mensagem apagada".
  '[deleted]': { labelKey: 'chat.preview.deleted', icon: 'ri-forbid-2-line' },
}

/** Translation key + Remix icon for a media preview token, or null for text. */
export function chatMediaPreview(
  raw: string | null | undefined,
): { labelKey: string; icon: string } | null {
  return MEDIA_PREVIEWS[(raw ?? '').trim()] ?? null
}

/**
 * Strips the markdown subset down to the text a single-line preview can show.
 *
 * Link labels survive and their URLs are dropped — the label is what the author
 * wrote for a human to read. Fenced blocks keep their code for the same reason:
 * the fence is punctuation, the code is the message.
 */
export function stripChatMarkdown(content: string | null | undefined): string {
  let text = content ?? ''

  // 1) Fenced code blocks — drop the fences, keep the code.
  text = text.replace(/```([\s\S]*?)```/g, '$1')
  // 2) Inline code spans.
  text = text.replace(/`([^`\n]+)`/g, '$1')
  // 3) Markdown links — keep the label, drop the destination.
  text = text.replace(/\[([^\]\n]+)\]\((https?:\/\/[^\s()<]+)\)/g, '$1')
  // 4) Emphasis, in the renderer's order (** before __ before _).
  text = text.replace(/\*\*([^\n]+?)\*\*/g, '$1')
  text = text.replace(/__([^\n]+?)__/g, '$1')
  text = text.replace(/~~([^\n]+?)~~/g, '$1')
  text = text.replace(/(^|[\s(])_([^\n_]+)_(?=$|[\s).,;:!?])/g, '$1$2')

  // 5) List markers. Flattening to one line would run the items together
  //    ("build ok testes ok"), so the bullet is kept as a real glyph — it reads
  //    as a separator inline, which a leading hyphen does not.
  text = text.replace(/^[ \t]*-[ \t]+/gm, '• ')
  text = text.replace(/^[ \t]*(\d+)\.[ \t]+/gm, '$1. ')

  // 6) Collapse to the single line the preview surfaces actually render.
  return text.replace(/\s+/g, ' ').trim()
}
